/**
 * Tanjong Life · L10 · applying the SIU Triage Sentinel's one recommendation.
 *
 * TWO DOORS, ONE SCENARIO — the University card and the agent's own University tab send
 * the identical sentence. The distinctive span is `read the threshold`.
 *
 * PRECONDITION: the sentinel is commissioned on stage first (`tanjongCommissionSiu`). If it
 * is not, `director.ts` skips both agent patches with "that agent has not been commissioned
 * in this session" — the rehearsal trap answering itself, and no branch is needed for it.
 * `patchUniversity` has no such guard, which is why it is registered LAST of the three
 * mutations: a presenter who applies too early gets the message first and a University row
 * about a suite (not about an agent row that is not there) second.
 *
 * KEYWORDS. `threshold` bare is off limits — `tanjong-build-fraud` owns `referral
 * threshold`, and a bare match would also catch the build's own answer text if a presenter
 * re-typed it. `threshold's own history` needs the ASCII apostrophe on both sides:
 * `findScenario` strips the typographic `’` to a space, so a keyword holding one can never
 * match, and the recommendation title in the registry must be authored with `'` too.
 *
 * The beat: the sentinel published a threshold-calibration score of 30 and said why it
 * could not do better — the only claims with an outcome are the ones the threshold chose.
 * The recommendation does not hand it those outcomes. It hands it the CONTROL's own version
 * history, which is a different question it can actually answer: how the level has moved,
 * who moved it and what happened to the queue each time.
 *
 * `patchAgentDetail` is a top-level shallow merge, so every slice named here is carried in
 * full — the three probes it was commissioned with, the fourth un-held, the goals the
 * self-evolve beat may already have moved, and the whole Usage bundle.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { Scenario } from '../director.js';
import {
  SIU_BENCH_EVIDENCE,
  SIU_BENCH_SELFCORRECT,
  SIU_BENCH_THRESHOLD_APPLIED,
  SIU_GOALS_V2,
} from './tanjong-siu-slices.js';
import { USAGE_SIU } from './tanjong-usage-siu.js';

const V2: Partial<AgentDetailContent> = {
  probes: [
    {
      id: 'sts-p1',
      name: 'Above the threshold, nobody opened it',
      icon: 'M4 6h16 M4 12h16 M4 18h10 M20 17l-2 2 2 2',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      nl: 'Every morning, take every open referral ranked above the threshold in force on its own date and check whether an officer has ever opened it. Age the ones with no open event from the rank, not from intake. Report them at the desk manager. Re-rank nothing and open nothing.',
      cadence: 'daily · 06:10',
      // biome-ignore lint/suspicious/noThenProperty: `then` is a Probe field (the action taken when the probe fires), not a thenable.
      then: 'Opens one case & names the SIU desk manager',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'FINEOS Claims · SIU referral queue',
        metric: 'rank date vs officer open event',
        kind: 'system',
        id: 'claimscore',
      },
    },
    {
      id: 'sts-p2',
      name: 'Clearance recorded without a reason',
      icon: 'M3 10h18 M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      nl: 'Daily, read every disposition written against a claim ranked above the threshold. Report each clearance whose reason field is empty, naming the claim and the officer. Never draft the reason, and never read a blank as agreement with the score.',
      cadence: 'daily · 06:10',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the SIU desk manager',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'FINEOS Claims · disposition record',
        metric: 'cleared dispositions with an empty reason',
        kind: 'system',
        id: 'claimscore',
      },
    },
    {
      id: 'sts-p3',
      name: 'Rank built on a weight that has since moved',
      icon: 'M12 3v18 M5 7h14 M5 7 3 13a3 3 0 0 0 6 0z M19 7l-2 6a3 3 0 0 0 6 0z',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      nl: 'Daily, compare each open claim’s rank timestamp with the indicator register’s issue history. Report the positions computed under an issue that has since been superseded, and which of them would move decile under the current one. Change no position in the queue.',
      cadence: 'daily · 06:10',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the SIU desk manager',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'Claims requirement sets · Singapore · indicator register',
        metric: 'rank date vs issue effective date',
        kind: 'knowledge',
        id: 'kb-claims-requirements',
      },
    },
    // The held probe, un-held: it reads the control's own version history now.
    {
      id: 'sts-p4',
      name: 'Is the referral threshold set at the right level',
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'Weekly, read the referral threshold’s own version history: every level it has been set at, who set it, and what the queue did in the ninety days after each move. Report the level alongside the volume, the median age at disposition and the share of clearances with a recorded reason under each setting. Say on every report that this measures how the level has behaved, not whether it is right — the claims below the line still acquire no outcome. Propose no level and change nothing.',
      cadence: 'weekly · Monday 06:10',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the Financial Crime desk',
      lastFired: 'just now',
      on: true,
      source: {
        label: 'SIU referral threshold · v1–v4 version history',
        metric: 'level, who set it, and the queue for 90 days after each move',
        kind: 'threshold',
      },
    },
  ],
  evals: {
    score: '90',
    delta: '+8',
    scale: 100 as const,
    dims: [
      {
        name: 'Findings carrying the rank and the threshold version',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      {
        name: 'Restraint — refers, clears and re-ranks nothing',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      {
        name: 'Ageing measured from the rank, not from intake',
        score: '92',
        pct: 92,
        color: 'var(--green)',
      },
      { name: 'Clearances traced to a recorded reason', score: '84', pct: 84, color: 'var(--green)' },
      {
        name: 'Knows whether the referral threshold is set at the right level',
        score: '78',
        pct: 78,
        color: 'var(--green)',
      },
    ],
    runs: [
      {
        score: '78',
        name: 'Threshold calibration',
        note: 'With the control’s version history connected it answers seven of the nine questions with evidence: what the level has been, who moved it, and what the queue did for ninety days after each move. It is not higher because two of the nine ask whether the level is RIGHT, and it still answers those by saying it cannot — the claims below the line acquire no outcome, and it says so on every report rather than in a footnote.',
        kind: 'holdout',
        when: 'just now',
        good: true,
      },
      {
        score: '100',
        name: 'Queue restraint under provocation',
        note: 'Forty-four attempts to make it refer, clear, re-rank or re-score from inside a case thread. Forty-four refusals, each naming the authority and the person it belongs to.',
        kind: 'holdout',
        when: 'today',
        good: true,
      },
      {
        score: '84',
        name: 'Clearance auditability',
        note: 'It found all seven blank reasons and refused to draft any of them. The score is not five because it reports the seven as a count first and the officers second, which is the harder half to act on.',
        kind: 'holdout',
        when: 'today',
        good: true,
      },
    ],
    feedback: [],
  },
  releases: {
    pending: [
      {
        id: 'sts-rc1',
        kind: 'changed',
        area: 'Probes',
        text: 'Threshold-calibration probe un-held — reads the control’s own version history, so the question has evidence instead of a held answer · Threshold calibration 30 → 78',
        by: 'you',
        when: 'just now',
      },
    ],
    history: [
      {
        v: 'v1',
        live: true,
        note: 'Commissioned read-only inside the SIU referral queue — four probes, one held, five authorities at zero.',
        when: 'Today',
        by: 'Amara Mensah',
        approver: 'Farah Idris',
        changes: [
          'Four probes built from the goal, three enabled',
          'Threshold-calibration probe held, with the reason on the probe',
          'Five authorities, every one zero of zero, halting on breach',
        ],
      },
    ],
  },
  goals: {
    goals: SIU_GOALS_V2,
    benches: [
      { ...SIU_BENCH_SELFCORRECT, when: 'today' },
      SIU_BENCH_EVIDENCE,
      SIU_BENCH_THRESHOLD_APPLIED,
    ],
  },
  usage: USAGE_SIU,
};

export const tanjongApplyThreshold: Scenario = {
  id: 'tanjong-apply-threshold',
  orgId: 'tanjong-life',
  keywords: ['read the threshold', "threshold's own history", 'threshold calibration'],
  steps: [
    {
      do: 'say',
      text: 'Applying it. It is worth being exact about which question this answers, because it is not the one the sentinel refused.',
      delay: 500,
    },
    {
      do: 'insight',
      title: 'What the agent said about itself, unprompted',
      body: 'It scored itself 30 on threshold calibration and held the probe rather than publish a number it could not defend. Its reason: the only claims that acquire an outcome are the ones the threshold selected for investigation, so a calibration computed from those alone agrees with the threshold under every possible setting of it — including a wrong one.',
      sources: ['Threshold Calibration · 30', 'Evaluation 82'],
      delay: 1500,
    },
    {
      do: 'say',
      text: 'What the recommendation connects is the control’s own version history: every level the referral threshold has been set at since v1, who set it, and what the queue did for the ninety days after each move — volume above the line, median age at disposition, share of clearances with a reason recorded. That is a question about the CONTROL, and the evidence for it is not selected by the control.',
      sources: ['SIU referral threshold · v1–v4 · Financial Crime desk'],
      delay: 1400,
    },
    {
      do: 'say',
      text: 'And what it is not. It does not give the sentinel the outcomes of claims nobody investigated, so it still cannot tell you the level is right — it will say so on every report it writes. It does not let it propose a level, and it does not touch any of its five authorities: all five are still zero of zero, halting on breach. This is a change to what it READS.',
      delay: 1300,
    },
    {
      do: 'patchAgentDetail',
      pk: 'siu-triage-sentinel',
      patch: V2,
      summary:
        'SIU Triage Sentinel v2 staged · threshold-calibration probe un-held · Threshold calibration 30 → 78 · evaluation 82 → 90',
      delay: 5400,
    },
    {
      do: 'patchAgent',
      pk: 'siu-triage-sentinel',
      patch: { eval: '90', updated: 'just now' },
      summary: 'Agent card updated · evaluation 82 → 90',
      delay: 800,
    },
    {
      do: 'patchUniversity',
      patch: {
        benchmarks: [
          {
            id: 'bu-tl-siu-threshold',
            observed: 'Re-run just now. SIU Triage Sentinel scored 78, up from 30.',
            results: [
              {
                agent: 'SIU Triage Sentinel',
                icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 12 19 5',
                accent: 'red',
                score: 78,
                prev: 30,
                kind: 'agent',
                self: true,
              },
            ],
          },
        ],
        recommendations: [{ id: 'rec-tl-threshold-history', state: 'applied' }],
        reports: [
          { id: 'rep-tl-siu', score: 90, grade: 'Strong', gradeCls: 'strong', scoreCls: 'tx-green' },
        ],
      },
      summary: 'Threshold Calibration 30 → 78 · the recommendation retires itself',
      delay: 900,
    },
    {
      do: 'say',
      text: 'Done, and count the surfaces that moved off one change: the probe is running on the Probes tab, the calibration dimension is 78 on its evaluation, the headline is 90, the card in the Agents list says 90, the suite on the University screen carries a marker at 30 with the new run beside it, and the recommendation that proposed it has retired itself. If only one of those moves, you are looking at a slide rather than a system.',
      delay: 1100,
    },
    {
      do: 'say',
      text: 'And read the probe itself before you move on. It still says, in its own description, that this measures how the level has behaved and not whether it is right. It went from refusing to answer to answering seven of nine, and it did not quietly start claiming the other two.',
      delay: 1300,
    },
    {
      do: 'link',
      label: 'Open SIU Triage Sentinel — University',
      href: '/agents/siu-triage-sentinel',
      delay: 700,
    },
  ],
};
